import Avvir, { ApiPipeline, ApiProjectPurposeType, ApiScanDatasetPurposeType, RunningProcessStatus } from "avvir";

import { AvvirThunk } from "./make_eventful_action";
import pipelineRunningProcessChecked, { PipelineRunningProcessCheckedEvent } from "../events/pipeline_running_process_checked";
import triggerFileDownload from "../services/utilities/general/trigger_file_download";
import pollData from "../services/utilities/general/poll_data";
import FilenameParser from "../services/utilities/filename_parser";


// The thunk returns pollData's promise directly. pollData resolves to the final pipeline on success,
// but to `void` when a poll rejects and its error handler swallows the error (see the onError callback
// below) — so the resolved type is `ApiPipeline | void`. No caller reads the resolved value; the union
// is just the honest type for "succeeded with a pipeline, or failed and was handled".
const checkPipelineStatus = (pipeline: ApiPipeline, triggerDownload: typeof triggerFileDownload = triggerFileDownload): AvvirThunk<Promise<ApiPipeline | void>, PipelineRunningProcessCheckedEvent> => {
  return (dispatch, getState) => {
    const associationIds = { projectId: pipeline?.firebaseProjectId, floorId: pipeline?.firebaseFloorId, scanDatasetId: pipeline?.firebaseScanDatasetId };
    const user = getState().user;
    return pollData(() => Avvir.api.pipelines.checkPipelineStatus(associationIds, pipeline?.id, user),
      (pipeline) => pipeline.status === "RUNNING",
      (pipeline) => dispatch(pipelineRunningProcessChecked(pipeline)),
      (pipeline) => {
        if (pipeline.status === "COMPLETED" && pipeline.name === "generate-bcf") {
          Avvir.api.files.getScanDatasetFiles(associationIds, ApiScanDatasetPurposeType.DEVIATIONS_SCAN_BCF, user).then(files => {
            triggerDownload(files.files[0].url, "deviatedElements.bcf");
          });
        } else if (pipeline.status === "COMPLETED" && pipeline.name === "create-project-report") {
          Avvir.api.files.listProjectFiles(associationIds, user, ApiProjectPurposeType.PROJECT_EXPORT_REPORT).then(files => {
            triggerDownload(files.at(-1).url, "projectExportReport.tsv");
          });
        } else if (pipeline.status === "COMPLETED" && pipeline.name === "generate-batch-ifc") {
          Avvir.api.files.listProjectFiles(
            associationIds, user, ApiProjectPurposeType.IFC_BATCH_ZIP,
          ).then(files => {
            // Gated behind ifcDownloadImprovements: only the wizard flow honors the
            // backend-produced filename. With the flag off we keep the historical
            // fixed "ifc-export.zip" name so the legacy export is byte-for-byte unchanged.
            // Read from the global flag bag because this thunk runs outside React (also
            // reached from run_pipeline), where useFlags() isn't available.
            const ifcDownloadImprovements = window.Avvir?.featureFlags?.flagsReadOnly?.ifcDownloadImprovements;
            if (ifcDownloadImprovements) {
              const file = files.at(-1);
              if (file) {
                // Honor the filename the backend produced (it follows the export
                // naming convention shown in the wizard preview) rather than
                // overriding it with a fixed name.
                const fileName = file.originalFileName || new FilenameParser().filenameFromUrl(file.url) || "ifc-export.zip";
                triggerDownload(file.url, fileName);
                dispatch(pipelineRunningProcessChecked({ ...pipeline, downloadUrl: file.url, downloadFileName: fileName }));
              }
            } else {
              triggerDownload(files.at(-1).url, "ifc-export.zip");
            }
          });
        }
        dispatch(pipelineRunningProcessChecked(pipeline));
      },
      // onError: runs when a status poll rejects (e.g. a network/gateway failure). Without this the
      // rejection was unhandled and the download toast spun forever; here we flip the process to FAILED
      // so the toast shows the failed/Retry state. startTime is stamped so the freshly-failed pipeline
      // still passes the overlay's recency filter and the toast actually renders.
      () => {
        dispatch(pipelineRunningProcessChecked({ ...pipeline, status: RunningProcessStatus.FAILED, startTime: pipeline?.startTime ?? Date.now() }));
      });
  };
};

export default checkPipelineStatus;
